import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import AnswerPanel from 'components/customer/answer_panel';
import connect from 'components/lib/connect';
import { MIN_SIDE_PANEL_WIDTH } from 'components/customer/lib/side_panel_width';
import SidePanel, { SidePanelType } from 'models/side_panel';
import TaskCommentPanel from 'components/customer/task_comments/task_comment_panel';
import TeamAssistPanel from 'components/customer/team_assist/team_assist_panel';
import useIsTeamAssistEnabled from 'components/hooks/use_is_team_assist_enabled';
import useSidePanelResize from 'components/customer/lib/use_side_panel_resize';

function CustomerSidePanel({ isAnswersReadEnabled, isSidePanelResizeEnabled, sidePanel, sidePanelWidth }) {
  const { t } = useTranslation();
  const isTeamAssistEnabled = useIsTeamAssistEnabled();
  const { isResizable, isResizing, maxWidth, width, wrapperRef, ...handlers } = useSidePanelResize({
    isEnabled: isSidePanelResizeEnabled,
    storedWidth: sidePanelWidth,
  });

  const panel = selectPanel({ isAnswersReadEnabled, isTeamAssistEnabled, sidePanel });
  if (!panel) {
    return null;
  }

  return (
    <PanelWrapper $width={isSidePanelResizeEnabled ? width : null} ref={wrapperRef}>
      {isResizable && (
        <ResizeHandle
          aria-label={t('Resize panel')}
          aria-orientation="vertical"
          aria-valuemax={maxWidth}
          aria-valuemin={MIN_SIDE_PANEL_WIDTH}
          aria-valuenow={width}
          data-aid="sidePanelResizeHandle"
          data-resizing={isResizing}
          onDoubleClick={handlers.onHandleDoubleClick}
          onKeyDown={handlers.onHandleKeyDown}
          onKeyUp={handlers.onHandleKeyUp}
          onPointerDown={handlers.onHandlePointerDown}
          role="separator"
          tabIndex={0}
        />
      )}
      {panel}
    </PanelWrapper>
  );
}

CustomerSidePanel.propTypes = {
  isAnswersReadEnabled: PropTypes.bool,
  isSidePanelResizeEnabled: PropTypes.bool,
  sidePanel: PropTypes.instanceOf(SidePanel),
  sidePanelWidth: PropTypes.number,
};

function selectPanel({ isAnswersReadEnabled, isTeamAssistEnabled, sidePanel }) {
  if (!sidePanel || !sidePanel.isOpen()) {
    return null;
  }

  if (sidePanel.isShowing(SidePanelType.ANSWER) && isAnswersReadEnabled) {
    return <AnswerPanel />;
  }

  if (sidePanel.isShowing(SidePanelType.TASK)) {
    return <TaskCommentPanel />;
  }

  if (isTeamAssistEnabled && sidePanel.isShowing(SidePanelType.AI_ASSIST)) {
    return <TeamAssistPanel />;
  }

  return null;
}

function mapStateToProps({ getProvider, isFeatureEnabled }) {
  const sidePanel = getProvider('sidePanel').get();
  const isAnswersReadEnabled = isFeatureEnabled('answers:read');
  const agentPreferences = getProvider('agentPreferences').get();

  return {
    isAnswersReadEnabled,
    isSidePanelResizeEnabled: isFeatureEnabled('sidePanelResize'),
    sidePanel,
    sidePanelWidth: (agentPreferences && agentPreferences.sidePanelWidth) || 0,
  };
}

export default connect(mapStateToProps)(CustomerSidePanel);

// A row too narrow to hold the 360px profile beside the panel leaves the rest of
// the column overflowing underneath it, so the panel has to cover what it
// overlaps: none of the three panels paints a background of its own, and the
// z-index clears the feed's overlays (its filter chip sits at 6).
const PanelWrapper = styled.div`
  background-color: ${p => p.theme.colors.defaultBackground};
  display: flex;
  height: 100%;
  min-width: 0;
  position: relative;
  z-index: 7;
  ${p => (p.$width ? draggedWidth : unresizableWidth)}
`;

const draggedWidth = css`
  flex: 0 0 ${p => p.$width}px;
  width: ${p => p.$width}px;
`;

// What the panel measured before it could be dragged.
const unresizableWidth = css`
  flex: 0 0 25%;
  max-width: 360px;
  min-width: 200px;
  width: 25%;
`;

const ResizeHandle = styled.div`
  bottom: 0;
  cursor: col-resize;
  left: -3px;
  position: absolute;
  top: 0;
  /* Stops a touch drag on the handle being claimed as a scroll gesture, which
     would cancel the pointer stream mid-resize. */
  touch-action: none;
  width: 7px;
  z-index: 4;

  &::after {
    background-color: transparent;
    bottom: 0;
    content: '';
    left: 3px;
    position: absolute;
    top: 0;
    transition: background-color 0.15s ease-in-out;
    width: 1px;
  }

  &:hover::after {
    background-color: ${p => p.theme.colors.gray400};
  }

  &:focus-visible::after,
  &[data-resizing='true']::after {
    background-color: ${p => p.theme.colors.blue700};
    width: 2px;
  }
`;
